// ارتباط با سرور برای طرح‌های ذخیره‌شده. ارسال تکه‌تکه است (هر تکه ۱ مگابایت) و اگر اینترنت قطع شد، از همان
// جایی که سرور دارد ادامه می‌دهد — نه از اول.

const BASE = '/api/designs';

function apiError(data, status) {
  const err = new Error((data && data.message) || 'خطایی پیش آمد؛ دوباره امتحان کنید.');
  err.code = (data && data.error) || `http-${status}`;
  err.status = status;
  err.data = data;
  return err;
}

async function call(path, method = 'GET', body) {
  let resp;
  try {
    resp = await fetch(`${BASE}${path}`, {
      method,
      credentials: 'same-origin',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
    });
  } catch (e) {
    const err = new Error('اتصال به سرور برقرار نشد؛ اینترنت را بررسی کنید.');
    err.code = 'network';
    throw err;
  }
  let data = {};
  try {
    data = await resp.json();
  } catch (e) {
    /* */
  }
  if (!resp.ok) throw apiError(data, resp.status);
  return data;
}

// یک تکه با XHR (برای درصدِ پیشرفت). → { received }
// زمانِ ثابت (timeout) ندارد: روی اینترنتِ کند ارسالِ یک تکه ممکن است طول بکشد و قبلاً بعد از ۱۲۰ ثانیه — درست
// وقتی تکه تقریباً کامل رفته بود — قطع و از اول فرستاده می‌شد (نوار به ۱۰۰ می‌رسید و دوباره عقب می‌رفت). حالا
// فقط اگر مدتی هیچ پیشرفتی نبود (STALL_MS) قطع می‌شود
const STALL_MS = 60000;
function putChunk(id, name, offset, blob, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    let watchdog = null;
    const arm = () => {
      clearTimeout(watchdog);
      watchdog = setTimeout(() => xhr.abort(), STALL_MS);
    };
    xhr.open('PUT', `${BASE}/${encodeURIComponent(id)}/files/${encodeURIComponent(name)}?offset=${offset}`);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.upload.onprogress = (e) => {
      arm();
      if (e.lengthComputable && onProgress) onProgress(e.loaded);
    };
    // بعد از رفتنِ کاملِ تکه، منتظرِ جوابِ سرور هم حداکثر همین‌قدر
    xhr.upload.onload = arm;
    xhr.onload = () => {
      clearTimeout(watchdog);
      let data = {};
      try {
        data = JSON.parse(xhr.responseText);
      } catch (e) {
        /* */
      }
      if (xhr.status >= 200 && xhr.status < 300) resolve(data);
      else reject(apiError(data, xhr.status));
    };
    const net = () => {
      clearTimeout(watchdog);
      const err = new Error('اتصال قطع شد');
      err.code = 'network';
      reject(err);
    };
    xhr.onerror = net;
    xhr.onabort = net;
    arm();
    xhr.send(blob);
  });
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// ادامه‌ی ارسالِ یک طرحِ ساخته‌شده. files: { نام: Blob }. onProgress(sentBytes, totalBytes) — هیچ‌وقت عقب نمی‌رود
export async function uploadDesignFiles(id, files, onProgress) {
  const path = `/${encodeURIComponent(id)}/upload`;
  const status = await call(path);
  const chunkSize = Math.min(status.chunkSize || 1024 * 1024, 1024 * 1024);
  const received = { ...(status.received || {}) };
  const names = Object.keys(files);
  const total = names.reduce((s, n) => s + files[n].size, 0);
  const sentBefore = () => names.reduce((s, n) => s + Math.min(received[n] || 0, files[n].size), 0);
  let shown = 0;
  const report = (v) => {
    shown = Math.max(shown, Math.min(total, v));
    if (onProgress) onProgress(shown, total);
  };
  report(sentBefore());

  // بعد از قطعی: سرور الان دقیقاً تا کجا را دارد؟ (شاید تکه‌ی قبلی رسیده بود و فقط جوابش نیامد)
  const resync = async () => {
    try {
      const st = await call(path);
      Object.assign(received, st.received || {});
    } catch (e) {
      /* هنوز وصل نیست — همان قبلی */
    }
  };

  for (const name of names) {
    const blob = files[name];
    let failures = 0;
    while ((received[name] || 0) < blob.size) {
      const offset = received[name] || 0;
      const part = blob.slice(offset, offset + chunkSize);
      try {
        const base = sentBefore();
        const r = await putChunk(id, name, offset, part, (loaded) => report(base + loaded));
        received[name] = r.received;
        failures = 0;
        report(sentBefore());
      } catch (e) {
        if (e.code === 'offset-mismatch' && e.data && typeof e.data.received === 'number') {
          received[name] = e.data.received; // سرور جای دیگری است — از همان‌جا
          continue;
        }
        const retriable = e.code === 'network' || (e.status && e.status >= 500) || e.status === 429;
        failures += 1;
        if (!retriable || failures > 8) throw e;
        await sleep(Math.min(20000, 1000 * 2 ** (failures - 1))); // ۱، ۲، ۴ … ثانیه
        await resync();
      }
    }
  }
  return call(`/${encodeURIComponent(id)}/complete`, 'POST', {});
}

// meta: { title, renderSource, jobId?, samples?, lightmapSize?, lightmapIntensity? }. files: { نام: Blob }
// → { id } (بعد uploadDesignFiles)
export function createDesign(meta, files) {
  const sizes = Object.fromEntries(Object.entries(files).map(([n, b]) => [n, b.size]));
  return call('', 'POST', { ...meta, files: sizes });
}

export const listMyDesigns = () => call('?mine=1');
export const listUserDesigns = (userId) => call(`?user=${encodeURIComponent(userId)}`);
export const getDesign = (id) => call(`/${encodeURIComponent(id)}`);
// حذفِ طرح/آگهیِ خودِ کاربر (روی سرور ۳۰ روز در سطل می‌ماند، بعد کامل پاک می‌شود)
export const deleteDesign = (id) => call(`/${encodeURIComponent(id)}`, 'DELETE');

// اکسپلور؛ province خالی = کلِ ایران. kind: 'all' (همه‌ی طراحی‌ها) | 'real' (فقط آگهی‌های مشاورین) → { items, next }
export const exploreDesigns = (province, offset = 0, limit = 24, kind = 'all') =>
  call(`/explore?province=${encodeURIComponent(province || '')}&kind=${kind === 'real' ? 'real' : 'all'}&offset=${offset}&limit=${limit}`);

// +۱ بازدید (سرور هر IP را برای هر طرح هر ۶ ساعت یک بار می‌شمارد)
export const markViewed = (id) => call(`/${encodeURIComponent(id)}/view`, 'POST', {}).catch(() => null);

// پروفایلِ عمومیِ یک کاربر (صفحه‌ی مشاور) → { user: { id, firstName, lastName, role, bio, phone }, stats }
export async function getUserProfile(userId) {
  let resp;
  try {
    resp = await fetch(`/api/auth/users/${encodeURIComponent(userId)}`, { credentials: 'same-origin' });
  } catch (e) {
    const err = new Error('اتصال به سرور برقرار نشد؛ اینترنت را بررسی کنید.');
    err.code = 'network';
    throw err;
  }
  let data = {};
  try {
    data = await resp.json();
  } catch (e) {
    /* */
  }
  if (!resp.ok) throw apiError(data, resp.status);
  return data;
}

// ذخیره‌شده‌های حساب (کاربرِ واردشده)
export const fetchSavedDesigns = () => call('/saved');
export const putSavedDesign = (id) => call(`/saved/${encodeURIComponent(id)}`, 'PUT');
export const deleteSavedDesign = (id) => call(`/saved/${encodeURIComponent(id)}`, 'DELETE');
export const mergeSavedDesigns = (items) => call('/saved/merge', 'POST', { items });

// جست‌وجوی آگهی‌ها (تبِ «جست‌وجو») — params: همان فیلترهای سرور (deal، type، province، q، priceMin … ، rooms، amen، sort)
export function searchDesigns(params, offset = 0, limit = 20) {
  const qs = new URLSearchParams();
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length)) return;
    qs.set(k, Array.isArray(v) ? v.join(',') : String(v));
  });
  qs.set('offset', String(offset));
  qs.set('limit', String(limit));
  return call(`/search?${qs.toString()}`);
}
